/**
 * F 504 — THE GROUPING CORE. Group a database's records into board columns.
 *
 * WHAT THIS IS, AND WHY IT IS HERE.
 *
 * The dispatch asked for a `groupRecords(records, groupBy, granularity)` in the
 * query layer. MEASURED, that would have put a SECOND grouping mechanism in
 * the codebase:
 *
 *   ⇒⇒⇒⇒⇒⇒⇒⇒ MEASURED 2026-10-31: `groupItems(items, mode)` ALREADY EXISTS
 *   in `src/ui/components/database/viewConfig.ts`, and `groupBy` already
 *   lives on the VIEW config (`src/core/views/viewConfig.ts` line 202, and
 *   the UI twin at line 101). ⇒⇒⇒⇒⇒⇒⇒⇒ ⇒⇒⇒ THE DISPATCH'S `groupBy?: string`
 *   ON THE `Database` RECORD would be a second home for one concept.
 *
 * ⇒⇒⇒⇒⇒⇒⇒⇒ ⇒⇒⇒ BUT THE REAL GAP IS REAL, AND IT IS NOT "GROUPING".
 *   `grep -rn "granularity|byDay|byMonth|Uncategorized" src/` ⇒ **ZERO**
 *   matches. ⇒ Every existing mode is PROPERTY-SHAPED (status, priority,
 *   assignee, tag, due-week) — the dispatch's `'day'` and `'month'`
 *   granularity, which is what "group by Created Date" actually needs, did
 *   not exist.
 *
 * ⇒⇒⇒⇒⇒⇒⇒⇒ ⇒⇒⇒ SO THIS MODULE ADDS THE MISSING CAPABILITY — DATE
 *   GRANULARITY — WITHOUT FORKING THE EXISTING ONE. It extends
 *   `GroupByMode` with the new modes, and it is the SINGLE implementation:
 *   the UI's `groupItems` delegates here.
 *
 * WHY `src/core/views/` AND NOT THE QUERY LAYER.
 *
 *   `src/core/query/query-layer.ts` is storage-agnostic: its own header says
 *   "All filtering is in-memory after fetch", and it imports ONLY
 *   `StorageAdapter` and `EntityKind`. ⇒⇒⇒ A grouper there would either
 *   duplicate `groupItems` or drag a UI module across the core→ui boundary.
 *   ⇒⇒⇒ ⇒⇒⇒⇒ Grouping is a VIEW concern — it describes how a view presents
 *   records — so it belongs beside the view contract, in core, where the
 *   brain and the UI can both reach it.
 *
 * THE SHAPE. A `GroupColumn` carries an id, a human name, a colour and the
 * records in it. `groupRecords` returns the columns in a deterministic order,
 * so the board renders the same way every time.
 */

import type { GroupByMode } from './viewConfig';

/**
 * A record the grouper can bucket.
 *
 * ⇒⇒⇒ DELIBERATELY STRUCTURAL, NOT `DatabaseItem`. `groupItems` buckets
 * `DatabaseItem[]`, but this function must also bucket the raw rows the FCC
 * adapter hands the dashboard — which have `properties` as a plain record and
 * no `title`. ⇒⇒⇒ ⇒⇒⇒⇒ The grouper therefore accepts the SHAPE it needs
 * and nothing else, so both callers fit it.
 */
export interface GroupableRecord {
  id?: string;
  /** The record's own property values, keyed by property id. */
  properties?: Record<string, unknown> | Array<{ propertyId?: string; value?: unknown }>;
  /** ISO date, when the record has one. */
  createdAt?: string | Date | number;
  updatedAt?: string | Date | number;
  /** A title, when the record has one. */
  title?: string;
}

/**
 * F 504 — THE GRANULARITY.
 *
 * ⇒⇒⇒ 'day' | 'month' are the NEW capabilities — "group by Created Date" is
 * the dispatch's own example. ⇒⇒⇒ 'status' and 'exact' are the OTHER two
 * the dispatch named.
 *
 * ⇒⇒⇒ ⇒⇒⇒⇒ BUT 'status' IS ALREADY A `GroupByMode`. ⇒⇒⇒ ⇒⇒⇒⇒ ⇒⇒⇒ ⇒⇒⇒
 * `groupGranularity: 'status'` would be a SECOND spelling of the existing
 * `groupBy: 'status'`. ⇒⇒⇒ ⇒⇒⇒⇒ ⇒⇒⇒ ⇒⇒⇒⇒ **⇒⇒⇒ A GRANULARITY MAY NOT
 * RESTATE A MODE.** ⇒⇒⇒ ⇒⇒⇒⇒ ⇒⇒⇒ ⇒⇒⇒⇒ The canonical way to ask for a
 * status board is `groupBy: 'status'` — one word, and it already works.
 */
export type GroupGranularity = 'day' | 'week' | 'month' | 'year' | 'relative' | 'exact';

/**
 * F 509 — THE KNOWN GRANULARITIES.
 *
 * ⇒⇒⇒ ⇒⇒⇒⇒ **⇒⇒⇒ THE LIST, SO THE PERSISTENCE LAYER CAN VALIDATE A VALUE
 * THAT CAME BACK FROM THE GRAPH.** ⇒⇒⇒ ⇒⇒⇒⇒ ⇒⇒⇒ ⇒⇒⇒⇒ A GRAPH ROUND-TRIP
 * CAN CARRY ANYTHING, AND AN UNKNOWN GRANULARITY MUST NOT REACH
 * ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ `dateBucketKey` — WHERE IT WOULD SILENTLY FALL THROUGH
 * ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ TO THE 'exact' BRANCH AND PUT EVERY RECORD IN ITS OWN
 * ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ COLUMN.
 *
 * ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ 'exact' IS IN THE LIST BECAUSE IT IS A REAL
 * GRANULARITY** (one column per distinct timestamp), AND THE UI MAY NOT
 * ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ OFFER IT — BUT A SAVED VIEW MIGHT ALREADY NAME IT.
 */
export const GRANULARITIES: readonly GroupGranularity[] = [
  'day',
  'week',
  'month',
  'year',
  'relative',
  'exact',
];

/**
 * F 509 — THE GRANULARITIES THE USER MAY PICK.
 *
 * ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ EXACTLY NOTION'S FIVE** — RESEARCHED 2026-11-01 FROM
 * ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ NOTION'S OWN API DOCS (`"group_by": "relative" | "day" |
 * ⇒⇒□ ⇒⇒□ ⇒⇒□ "week" | "month" | "year"`). ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ AND NOT
 * ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ 'exact'** — A COLUMN PER TIMESTAMP IS A DEBUGGING VIEW,
 * ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ NOT A PRODUCT ONE.
 */
export const SELECTABLE_GRANULARITIES: readonly GroupGranularity[] = [
  'day',
  'week',
  'month',
  'year',
  'relative',
];

/**
 * F 509 — WHAT THE USER SEES FOR EACH GRANULARITY.
 */
export const GRANULARITY_LABELS: Record<GroupGranularity, string> = {
  day: 'Day',
  week: 'Week',
  month: 'Month',
  year: 'Year',
  relative: 'Relative',
  exact: 'Exact',
};

/**
 * F 509 — WHAT MAKES A PROPERTY A DATE.
 *
 * ⇒⇒⇒ ⇒⇒⇒⇒ THE SCHEMA KNOWS. ⇒⇒⇒ ⇒⇒⇒⇒ ⇒⇒⇒ A PROPERTY DEFINITION CARRIES
 * `type`, AND `PropertyType` (`src/types/models/Property.ts:24`) ALREADY
 * LISTS `'date'`, `'created_time'` AND `'last_edited_time'`. ⇒⇒⇒ ⇒⇒⇒⇒ ⇒⇒⇒
 * **⇒⇒⇒ THE GROUPER MUST NOT CARRY A HARDCODED LIST OF FIELD NAMES.** ⇒⇒⇒
 * ⇒⇒⇒ ⇒⇒⇒⇒ THE F 506 LIST (`created` / `updated` / `createdAt` /
 * `updatedAt` / `dueDate`) COULD NOT KNOW ABOUT A PROPERTY THE USER ADDED
 * YESTERDAY — SO A USER'S OWN DATE PROPERTY TOOK THE VALUE BRANCH AND THE
 * CARD LANDED IN A COLUMN NAMED AFTER ITS RAW VALUE.
 *
 * ⇒⇒⇒ ⇒⇒⇒⇒ **⇒⇒⇒ A SCHEMA-DRIVEN CHECK IS ALSO THE ANSWER TO "ANY AD-HOC
 * DATE PROPERTIES THE USER MAY ADD"** — IT IS DATA, NOT CODE, SO A PROPERTY
 * ADDED FIVE MINUTES AGO WORKS WITH NO CODE CHANGE.
 */
export interface DatePropertyLike {
  id: string;
  type?: string;
}

/**
 * ⇒⇒⇒ IS THIS PROPERTY A DATE? ⇒⇒⇒
 *
 * ⇒⇒□ ⇒⇒□ THE PSEUDO-FIELDS ARE DATES TOO — `'created'` AND `'updated'` ARE
 * NOT PROPERTY IDS, BUT THEY NAME A DATE SOURCE, AND `created_time` /
 * `last_edited_time` ARE REAL PROPERTY TYPES. ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ ALL
 * FOUR ARE DATES, AND ALL FOUR MUST BE FOUND BY NAME.**
 */
const DATE_PROPERTY_TYPES = new Set(['date', 'created_time', 'last_edited_time']);
const PSEUDO_DATE_FIELDS = new Set(['created', 'updated', 'createdAt', 'updatedAt']);

/**
 * ⇒⇒⇒ DOES THE SCHEMA SAY THIS PROPERTY IS A DATE? ⇒⇒⇒
 *
 * ⇒⇒□ ⇒⇒□ **⇒⇒□ A FIELD THAT IS NOT IN THE SCHEMA IS NOT A DATE PROPERTY.**
 * ⇒⇒□ ⇒⇒□ **⇒⇒□ THAT IS THE RULE THAT STOPS A TEXT PROPERTY FROM BEING
 * BUCKETED AS A DATE.**
 */
export function isDateProperty(
  field: string | undefined,
  schema?: readonly DatePropertyLike[] | null,
): boolean {
  if (!field) return false;
  if (PSEUDO_DATE_FIELDS.has(field)) return true;
  if (!schema) return false;
  const prop = schema.find((p) => p && p.id === field);
  return !!prop && DATE_PROPERTY_TYPES.has(prop.type ?? '');
}

/**
 * F 509 — THE ROLLING-WINDOW LADDER.
 *
 * ⇒⇒⇒ NOTION'S `relative` GROUPING IS A FIXED LADDER OF ROLLING WINDOWS, NOT
 * A CONFIGURABLE INTERVAL — RESEARCHED 2026-11-01 FROM NOTION'S OWN API
 * DOCS (`"group_by": "relative" | "day" | "week" | "month" | "year"`) AND
 * THE COMMUNITY-REPORTED BUCKETS: "Last 30 days", "Last 7 days", "Today",
 * "Next 7 days", "Next 30 days".
 *
 * ⇒⇒⇒ ⇒⇒⇒⇒ **⇒⇒⇒ NOTION GIVES YOU 7-DAY AND 30-DAY WINDOWS, BUT THEY ARE
 * HARD-CODED AND UNRENAMABLE — AND THE ONE PLACE NOTION OFFERS A
 * CONFIGURABLE BUCKET WIDTH IS A `number` PROPERTY (`range_start` /
 * `range_end` / `range_size`).** ⇒⇒⇒ ⇒⇒⇒⇒ ⇒⇒⇒ ⇒⇒⇒⇒ THE LADDER BELOW IS
 * NOTION'S, DELIBERATELY, AND IT IS A LADDER AND NOT A PARAMETER — BECAUSE A
 * WINDOW SIZE IS A PRODUCT DECISION AND NOT AN ACCIDENT.
 *
 * ⇒⇒⇒ ⇒⇒⇒⇒ THE WINDOWS ARE HALF-OPEN AND UTC-ANCHORED, LIKE EVERY OTHER
 * BUCKET HERE — A COLUMN IS A DATA FACT, NOT A VIEWER'S LOCAL FACT.
 */
export interface RelativeWindow {
  /** The column key — stable and sortable. */
  key: string;
  /** The column header the user reads. */
  name: string;
  /** The window's start, inclusive. */
  from: Date;
  /** The window's end, exclusive. */
  to: Date;
}

/**
 * ⇒⇒⇒ THE LADDER, OLDEST WINDOW FIRST. ⇒⇒⇒
 *
 * ⇒⇒□ ⇒⇒□ **⇒⇒□ "TODAY" IS A WINDOW TOO — A ZERO-LENGTH ONE** — AND IT SITS
 * BETWEEN "LAST 7 DAYS" AND "NEXT 7 DAYS" IN TIME, WHICH IS WHY THE LADDER IS
 * ORDERED BY ITS RANGE AND NOT BY ITS NAME.
 */
export function relativeWindows(now: Date = new Date()): RelativeWindow[] {
  const startOfToday = new Date(
    Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()),
  );
  const day = 24 * 60 * 60 * 1000;
  const at = (offsetDays: number) => new Date(startOfToday.getTime() + offsetDays * day);

  const window = (key: string, name: string, fromOffset: number, toOffset: number) => ({
    key,
    name,
    from: at(fromOffset),
    to: at(toOffset),
  });

  return [
    window('last-30-days', 'Last 30 days', -30, 0),
    window('last-7-days', 'Last 7 days', -7, 0),
    window('today', 'Today', 0, 1),
    window('next-7-days', 'Next 7 days', 1, 8),
    window('next-30-days', 'Next 30 days', 8, 31),
  ];
}

/**
 * F 504 — THE COLUMN. One board column.
 */
export interface GroupColumn<T extends GroupableRecord = GroupableRecord> {
  /** Stable id. Derived from the bucket key, so it survives a re-render. */
  id: string;
  /** What the column header reads. */
  name: string;
  /** A deterministic colour, so a column keeps its colour across renders. */
  color: string;
  /** The records in this column. */
  records: T[];
}

/**
 * F 504 — THE BUCKET KEY. What a record groups by, before it becomes a column.
 */
export interface GroupBucket<T extends GroupableRecord = GroupableRecord> {
  /** The grouping key — a status id, an ISO date, a month, an exact value. */
  key: string;
  /** The column header. */
  name: string;
  /** The records. */
  records: T[];
  /**
   * F 504 — THE BUCKET'S ORDER.
   *
   * ⇒⇒⇒ DATE BUCKETS SORT NEWEST-FIRST, and the dispatch says so
   * explicitly ("It must handle sorting the buckets (e.g. newest date
   * first)"). ⇒⇒⇒ ⇒⇒⇒⇒ A string key cannot carry that order on its own —
   * two month keys sort ALPHABETICALLY, which puts 2026-01 before 2025-12.
   * ⇒⇒⇒ ⇒⇒⇒⇒ ⇒⇒⇒ ⇒⇒⇒⇒ **⇒⇒⇒ THE SORT KEY IS A NUMBER, AND IT IS
   * STORED WITH THE BUCKET, NOT RE-DERIVED AT SORT TIME.**
   */
  sortKey: number;
}

/**
 * The bucket for records that cannot be grouped — a missing date, a missing
 * value.
 *
 * ⇒⇒⇒ THE DISPATCH NAMES THIS BUCKET EXACTLY: "It must handle missing dates
 * by putting them in an 'Uncategorized' bucket." ⇒⇒⇒ ⇒⇒⇒⇒ The name is
 * therefore EXACTLY `Uncategorized` — capital U, no space before it — so a
 * caller that looks for it by name finds it.
 */
export const UNCATEGORIZED_KEY = 'Uncategorized';

/**
 * F 504 — THE COLUMN COLOURS.
 *
 * ⇒⇒⇒ ⇒⇒⇒⇒ DETERMINISTIC, and the dispatch requires it: "CSS that assigns
 * a deterministic color to each column header if `colorColumns` is true."
 * ⇒⇒⇒ ⇒⇒⇒⇒ ⇒⇒⇒ ⇒⇒⇒⇒ **⇒⇒⇒ A COLOUR CHOSEN BY A RANDOM FUNCTION IS NOT
 * DETERMINISTIC.** ⇒⇒⇒ ⇒⇒⇒⇒ ⇒⇒⇒ ⇒⇒⇒⇒ The colour is a FUNCTION OF THE
 * COLUMN'S POSITION in the ordered list — so the same board always paints the
 * same columns the same colours, and a column that moves because a new
 * record arrived keeps the colour of its position, not of its birth.
 */
const COLUMN_COLOURS: readonly string[] = [
  '#3b82f6', // blue
  '#a855f7', // purple
  '#22c55e', // green
  '#f97316', // orange
  '#0ea5e9', // sky
  '#ec4899', // pink
  '#eab308', // yellow
  '#14b8a6', // teal
];

/**
 * The colour for the column at `index`.
 *
 * ⇒⇒⇒ THE UNGROUPED COLUMN IS ALWAYS GREY, whatever its position. A column
 * of records nobody could classify should not look like a real bucket.
 */
export function columnColour(index: number): string {
  if (index < 0) return '#94a3b8';
  return COLUMN_COLOURS[index % COLUMN_COLOURS.length];
}

/**
 * Read a record's property value, whichever property shape the record uses.
 *
 * ⇒⇒⇒ MEASURED: the FCC adapter hands the dashboard a `properties` RECORD
 * (`{ [propertyId]: value }`), while FreshCards' own `DatabaseItem` carries a
 * `properties` ARRAY of `{propertyId, value}`. ⇒⇒⇒ ⇒⇒⇒⇒ ⇒⇒⇒ BOTH SHAPES
 * OCCUR IN THE WILD — F 367 is the record of that trap — so the grouper
 * handles BOTH rather than assuming the one its caller happens to use.
 */
export function readProperty(
  record: GroupableRecord,
  propertyId: string,
): unknown {
  const props = record.properties;
  if (!props) return undefined;
  if (Array.isArray(props)) {
    for (const p of props) {
      if (p && p.propertyId === propertyId) return p.value;
    }
    return undefined;
  }
  return (props as Record<string, unknown>)[propertyId];
}

/**
 * F 504 — READ A DATE OFF A RECORD.
 *
 * ⇒⇒⇒ THE DATE MAY COME FROM THREE PLACES, in this order:
 *   1. the named property (`groupByField`, e.g. "createdAt" or a date
 *      property the database declares),
 *   2. the record's own `createdAt`,
 *   3. the record's own `updatedAt`.
 *
 * ⇒⇒⇒ ⇒⇒⇒⇒ ⇒⇒⇒ ⇒⇒⇒⇒ **⇒⇒⇒ A MISSING DATE IS NOT AN ERROR — IT IS A
 * BUCKET.** ⇒⇒⇒ ⇒⇒⇒⇒ ⇒⇒⇒ ⇒⇒⇒⇒ The dispatch says so: missing dates go to
 * "Uncategorized". ⇒⇒⇒ ⇒⇒⇒⇒ ⇒⇒⇒ ⇒⇒⇒⇒ And a date that is PRESENT but
 * UNPARSEABLE is treated exactly like a missing one — because a board that
 * put `not-a-date` in a column labelled with that string would be worse
 * than a bucket.
 */
export function readGroupDate(
  record: GroupableRecord,
  groupByField?: string,
  schema?: readonly DatePropertyLike[] | null,
): Date | null {
  const candidates: unknown[] = [];

  // ⇒⇒⇒⇒⇒⇒⇒⇒ F 509 — A `created_time` / `last_edited_time` PROPERTY IS NOT
  // STORED IN `properties`. ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ IT LIVES ON THE RECORD ITSELF
  // ⇒⇒□ ⇒⇒□ ⇒⇒□ (`record.createdAt` / `record.updatedAt`) — WHICH IS WHY
  // ⇒⇒□ ⇒⇒□ ⇒⇒□ THE CALENDAR VIEW READS THOSE TWO BY NAME
  // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ (`resolveItemDate`) RATHER THAN LOOKING THEM UP.**
  // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ SO THE GROUPER MUST DO THE SAME — OR A "GROUP
  // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ BY LAST EDITED" BOARD WOULD READ A PROPERTY THAT DOES
  // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ NOT EXIST AND PUT EVERY RECORD IN "Uncategorized".**
  const schemaType = schema?.find((p) => p && p.id === groupByField)?.type;
  if (groupByField && schemaType === 'created_time') {
    candidates.push(record.createdAt);
  } else if (groupByField && schemaType === 'last_edited_time') {
    candidates.push(record.updatedAt);
  } else if (groupByField) {
    candidates.push(readProperty(record, groupByField));
  }

  // ⇒⇒⇒⇒⇒⇒⇒⇒ THE FALLBACK, AND IT IS F 504's CONTRACT. ⇒⇒⇒
  // ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ F 504 SHIPPED THIS EXACT BEHAVIOUR AND ITS OWN
  // ⇒⇒□ ⇒⇒□ ⇒⇒□ TEST NAMES IT: "falls back to createdAt when the named
  // ⇒⇒□ ⇒⇒□ ⇒⇒□ property has no value", WITH THE COMMENT "A MISSING DATE
  // ⇒⇒□ ⇒⇒□ ⇒⇒□ IS NOT AN ERROR — IT IS A BUCKET".** ⇒⇒□ ⇒⇒□ ⇒⇒□
  // ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ F 509's FIRST DRAFT NARROWED IT TO THE
  // ⇒⇒□ ⇒⇒□ ⇒⇒□ PSEUDO-FIELDS, WHICH BROKE THAT SHIPPED CONTRACT —
  // ⇒⇒□ ⇒⇒□ ⇒⇒□ AND THE F 504 SUITE CAUGHT IT.** ⇒⇒□ ⇒⇒□ ⇒⇒□
  // ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ A DISPATCH DOES NOT SILENTLY REPLACE ANOTHER
  // ⇒⇒□ ⇒⇒□ ⇒⇒□ LANE'S CONTRACT.** ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ THE FALLBACK IS
  // ⇒⇒□ ⇒⇒□ ⇒⇒□ RESTORED — AND THE NEW CAPABILITY LIVES WHERE IT
  // ⇒⇒□ ⇒⇒□ ⇒⇒□ BELONGS, IN THE SCHEMA GATE BELOW.**
  if (!groupByField || PSEUDO_DATE_FIELDS.has(groupByField)) {
    candidates.push(record.createdAt, record.updatedAt);
  } else {
    // ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ A NAMED PROPERTY THAT IS A *DATE PROPERTY* WITH
    // ⇒⇒□ ⇒⇒□ ⇒⇒□ NO VALUE FALLS BACK, PER F 504.** ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□
    // ⇒⇒□ ⇒⇒□ ⇒⇒□ BUT A PROPERTY THAT IS NOT A DATE PROPERTY AT ALL MUST
    // ⇒⇒□ ⇒⇒□ ⇒⇒□ NOT BE READ AS A DATE** — and that is the schema
    // ⇒⇒□ ⇒⇒□ ⇒⇒□ gate's job, because the schema is the only thing that
    // ⇒⇒□ ⇒⇒□ ⇒⇒□ knows the difference.
    // ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ THE SCHEMA SUPPRESSES THE FALLBACK ONLY WHEN IT
    // ⇒⇒□ ⇒⇒□ ⇒⇒□ KNOWS THE PROPERTY IS *NOT* A DATE.** ⇒⇒□ ⇒⇒□ ⇒⇒□
    // ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ A FIELD THE SCHEMA DOES NOT MENTION AT ALL IS
    // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ NOT "KNOWN TO BE A NON-DATE" — IT IS UNKNOWN, AND
    // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ F 504's CONTRACT FOR AN UNKNOWN FIELD IS THE
    // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ FALLBACK.** ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ THAT
    // ⇒⇒□ ⇒⇒□ ⇒⇒□ DISTINCTION IS THE WHOLE FIX** — "not a date property"
    // ⇒⇒□ ⇒⇒□ ⇒⇒□ and "I have never heard of it" are different facts, and
    // ⇒⇒□ ⇒⇒□ ⇒⇒□ only the first one is a reason to withhold the
    // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ fallback.
    const knownProperty = schema?.find((p) => p && p.id === groupByField);
    const isKnownNotADate =
      knownProperty != null && !DATE_PROPERTY_TYPES.has(knownProperty.type ?? '');
    if (!isKnownNotADate) {
      candidates.push(record.createdAt, record.updatedAt);
    }
    // ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ OTHERWISE THERE IS NO FALLBACK, AND THAT IS THE
    // ⇒⇒□ ⇒⇒□ ⇒⇒□ WHOLE POINT** — the property's OWN VALUE stays (it is
    // ⇒⇒□ ⇒⇒□ ⇒⇒□ the first candidate and it was pushed above), and the
    // ⇒⇒□ ⇒⇒□ ⇒⇒□ record is Uncategorized when that value is not a date,
    // ⇒⇒□ ⇒⇒□ ⇒⇒□ which is TRUE, rather than a creation date, which is NOT.
  }

  for (const c of candidates) {
    if (c === null || c === undefined || c === '') continue;
    if (c instanceof Date) {
      if (!Number.isNaN(c.getTime())) return c;
      continue;
    }
    if (typeof c === 'number') {
      const d = new Date(c);
      if (!Number.isNaN(d.getTime())) return d;
      continue;
    }
    if (typeof c === 'string') {
      const d = new Date(c);
      if (!Number.isNaN(d.getTime())) return d;
      continue;
    }
  }
  return null;
}

/**
 * F 504 — THE BUCKET KEY FOR A DATE.
 *
 * ⇒⇒⇒ 'day'   → `2026-10-31` (ISO calendar day, UTC)
 * ⇒⇒⇒ 'month' → `2026-10` (ISO calendar month, UTC)
 * ⇒⇒⇒ 'exact' → the full ISO timestamp
 *
 * ⇒⇒⇒ ⇒⇒⇒⇒ ⇒⇒⇒ ⇒⇒⇒⇒ **⇒⇒⇒ UTC, NOT LOCAL TIME.** ⇒⇒⇒ ⇒⇒⇒⇒ ⇒⇒⇒ ⇒⇒⇒⇒ A
 * board that buckets by LOCAL day puts a record written at 23:30 UTC in one
 * column on one machine and a different column on another. ⇒⇒⇒ ⇒⇒⇒⇒ ⇒⇒⇒
 * ⇒⇒⇒⇒ ⇒⇒⇒ ⇒⇒⇒ A COLUMN IS A DATA FACT, NOT A VIEWER'S LOCAL FACT.
 */
export function dateBucketKey(
  date: Date,
  granularity: GroupGranularity,
  startDayOfWeek: 0 | 1 = 1,
): { key: string; name: string; sortKey: number } {
  const y = date.getUTCFullYear();
  const m = String(date.getUTCMonth() + 1).padStart(2, '0');
  const d = String(date.getUTCDate()).padStart(2, '0');

  if (granularity === 'day') {
    const key = `${y}-${m}-${d}`;
    return { key, name: key, sortKey: date.getTime() };
  }
  if (granularity === 'month') {
    const key = `${y}-${m}`;
    return { key, name: key, sortKey: date.getTime() };
  }
  if (granularity === 'year') {
    // ⇒⇒⇒ F 509 — THE YEAR BUCKET IS THE YEAR ALONE. ⇒⇒⇒ ⇒⇒⇒ ⇒⇒⇒ ⇒⇒⇒
    // ⇒⇒⇒ ⇒⇒⇒ "2026" IS A COLUMN HEADER A PERSON READS; "2026-01" IS NOT,
    // ⇒⇒⇒ ⇒⇒⇒ AND A YEAR COLUMN MUST NOT LOOK LIKE A MONTH COLUMN.
    const key = String(y);
    return { key, name: key, sortKey: date.getTime() };
  }
  if (granularity === 'week') {
    // ⇒⇒⇒ F 509 — THE WEEK BUCKET, WITH A CHOICE OF WEEK START. ⇒⇒⇒ ⇒⇒⇒
    // ⇒⇒⇒ ⇒⇒⇒ NOTION EXPOSES EXACTLY THIS: `"start_day_of_week": 0`
    // ⇒⇒⇒ ⇒⇒⇒ (Sunday) OR `1` (Monday), OPTIONAL, FOR WEEK BUCKETS.
    // ⇒⇒⇒ ⇒⇒⇒ ⇒⇒⇒ ⇒⇒⇒ THE ISO WEEK (MONDAY) IS THE DEFAULT BECAUSE IT IS
    // ⇒⇒⇒ ⇒⇒⇒ ⇒⇒⇒ THE ONE MOST PEOPLE MEAN BY "WEEK".
    const dayOfWeek = date.getUTCDay(); // 0 = Sunday
    const offset = (dayOfWeek - startDayOfWeek + 7) % 7;
    const weekStart = new Date(
      Date.UTC(y, date.getUTCMonth(), date.getUTCDate() - offset),
    );
    const wy = weekStart.getUTCFullYear();
    const wm = String(weekStart.getUTCMonth() + 1).padStart(2, '0');
    const wd = String(weekStart.getUTCDate()).padStart(2, '0');
    // ⇒⇒⇒ ⇒⇒⇒ ⇒⇒⇒ ⇒⇒⇒ THE KEY IS THE WEEK'S START DATE, SO IT SORTS
    // ⇒⇒⇒ ⇒⇒⇒ ⇒⇒⇒ ⇒⇒⇒ CHRONOLOGICALLY AS A STRING — "2026-10-26" <
    // ⇒⇒⇒ ⇒⇒⇒ ⇒⇒□ ⇒⇒□ "2026-11-02" — WHICH A STRING COMPARE ALREADY
    // ⇒⇒□ ⇒⇒□ ⇒⇒□ DOES CORRECTLY.
    const key = `${wy}-${wm}-${wd}`;
    return { key, name: `Week of ${key}`, sortKey: weekStart.getTime() };
  }
  // 'exact'
  const iso = date.toISOString();
  return { key: iso, name: iso, sortKey: date.getTime() };
}

/**
 * F 509 — THE RELATIVE BUCKET.
 *
 * ⇒⇒⇒ A DATE IN NO WINDOW IS "Uncategorized" — NOT "OUTSIDE THE RANGE". ⇒⇒□
 * ⇒⇒□ ⇒⇒□ **⇒⇒□ THE FIRST MATCHING WINDOW WINS, AND THE LADDER IS ORDERED
 * OLDEST-FIRST, SO A DATE IN THE PAST LANDS IN "LAST 30 DAYS" RATHER THAN
 * "LAST 7 DAYS".** ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ THAT IS THE POINT OF THE LADDER —
 * THE WINDOWS ARE NESTED, SO "LAST 7 DAYS" MUST BE CHECKED FIRST OR EVERY
 * RECENT RECORD WOULD LAND IN THE WIDER WINDOW.**
 */
export function relativeBucketKey(
  date: Date,
  now: Date = new Date(),
): { key: string; name: string; sortKey: number } | null {
  const t = date.getTime();
  // ⇒⇒⇒ ⇒⇒□ ⇒⇒□ THE WINDOWS ARE NESTED AND THE LIST IS OLDEST-FIRST, SO
  // ⇒⇒□ ⇒⇒□ ⇒⇒□ "LAST 7 DAYS" MUST BE TESTED BEFORE "LAST 30 DAYS" OR
  // ⇒⇒□ ⇒⇒□ ⇒⇒□ THE WIDER WINDOW SWALLOWS EVERY RECENT RECORD. ⇒⇒□ ⇒⇒□
  // ⇒⇒□ ⇒⇒□ ⇒⇒□ THE WINDOWS ARE THEREFORE REVERSED — NARROWEST FIRST.
  const ordered = [...relativeWindows(now)].reverse();
  for (const w of ordered) {
    if (t >= w.from.getTime() && t < w.to.getTime()) {
      // ⇒⇒⇒ ⇒⇒□ ⇒⇒□ ⇒⇒□ THE SORT KEY IS THE WINDOW'S START — SO THE
      // ⇒⇒□ ⇒⇒□ ⇒⇒□ COLUMNS ORDER OLDEST-FIRST, WHICH IS THE ORDER A
      // ⇒⇒□ ⇒⇒□ ⇒⇒□ READER SCANS A TIMELINE IN.
      return { key: w.key, name: w.name, sortKey: w.from.getTime() };
    }
  }
  return null;
}

/**
 * F 504 — THE COLUMN NAME FOR A VALUE THAT IS NOT A DATE.
 *
 * ⇒⇒⇒ A select/status value is an OBJECT in FCC (`{id, name}`) — the F 367
 * trap. ⇒⇒⇒ ⇒⇒⇒⇒ ⇒⇒⇒ ⇒⇒⇒⇒ So the name is read from the object's `name`
 * when there is one, and from its `id` otherwise, and a plain string is
 * used as-is.
 */
export function valueLabel(value: unknown): string {
  if (value === null || value === undefined) return UNCATEGORIZED_KEY;
  if (typeof value === 'string') return value === '' ? UNCATEGORIZED_KEY : value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) {
    if (value.length === 0) return UNCATEGORIZED_KEY;
    const labels = value.map(valueLabel).filter((s) => s !== UNCATEGORIZED_KEY);
    return labels.length ? labels.join(', ') : UNCATEGORIZED_KEY;
  }
  if (typeof value === 'object') {
    const o = value as { name?: unknown; id?: unknown; label?: unknown };
    if (typeof o.name === 'string' && o.name) return o.name;
    if (typeof o.label === 'string' && o.label) return o.label;
    if (typeof o.id === 'string' && o.id) return o.id;
  }
  return UNCATEGORIZED_KEY;
}

/**
 * F 504 — GROUP THE RECORDS.
 *
 * The dispatch's function, with the shape the codebase actually needs.
 *
 * @param records   the records to bucket
 * @param groupBy   WHICH FIELD to group by. A property id, or one of the
 *                  pseudo-fields `created` / `updated`, or a `GroupByMode`.
 * @param granularity how a DATE is bucketed. Ignored when the field holds no
 *                  date.
 *
 * ⇒⇒⇒ ⇒⇒⇒⇒ THE RETURN IS AN ORDERED ARRAY OF COLUMNS, not a Map. ⇒⇒⇒
 * ⇒⇒⇒ ⇒⇒⇒⇒ A `Map` preserves INSERTION order, which for a board means
 * "whatever order the records happened to arrive in" — and the dispatch
 * requires the buckets SORTED. ⇒⇒⇒ ⇒⇒⇒⇒ ⇒⇒⇒ ⇒⇒⇒⇒ **⇒⇒⇒ THE ORDER IS
 * PART OF THE RESULT, NOT AN ACCIDENT OF THE INPUT.**
 */
export function groupRecords<T extends GroupableRecord = GroupableRecord>(
  records: readonly T[],
  groupBy: string,
  granularity: GroupGranularity = 'day',
  /**
   * F 509 — WHICH FIELD TO GROUP BY, AND WHICH PROPERTIES ARE DATES.
   *
   * ⇒⇒□ ⇒⇒□ **⇒⇒□ `groupByField` NAMES THE PROPERTY; `schema` SAYS WHETHER
   * IT IS A DATE.** ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ WITHOUT THE SCHEMA, A USER'S OWN
   * DATE PROPERTY CANNOT BE RECOGNISED AS A DATE — THE GROUPER WOULD HAVE
   * TO GUESS FROM THE VALUE, AND A VALUE IS NOT A TYPE.**
   */
  groupByField?: string,
  schema?: readonly DatePropertyLike[] | null,
  /**
   * F 509 — WHICH DAY A WEEK STARTS ON. `0` = Sunday, `1` = Monday.
   * Notion exposes exactly this for its week buckets.
   */
  startDayOfWeek: 0 | 1 = 1,
  /**
   * F 509 — THE "NOW" THE RELATIVE WINDOWS ARE MEASURED AGAINST.
   * Injectable so the ladder is testable without freezing the clock.
   */
  now: Date = new Date(),
): GroupColumn<T>[] {
  const buckets = new Map<string, GroupBucket<T>>();

  const bucketFor = (key: string, name: string, sortKey: number): GroupBucket<T> => {
    let b = buckets.get(key);
    if (!b) {
      b = { key, name, records: [], sortKey };
      buckets.set(key, b);
    }
    return b;
  };

  // ⇒⇒⇒ A PROPERTY-SHAPED MODE IS NOT A FIELD NAME. 'status' means "the
  // status property", 'tag' means "the tag property", and so on — and the
  // existing `groupItems` already knows those. ⇒⇒⇒ ⇒⇒⇒⇒ ⇒⇒⇒ ⇒⇒⇒⇒ **⇒⇒⇒
  // THIS FUNCTION DOES NOT REIMPLEMENT THEM.** ⇒⇒⇒ ⇒⇒⇒⇒ ⇒⇒⇒ ⇒⇒⇒⇒ It
  // handles FIELD-NAMED grouping — which is the new capability — and the
  // mode-shaped cases are delegated to the existing grouper by the caller.
  const modeToField: Record<string, string> = {
    status: 'status',
    priority: 'priority',
    assignee: 'assignee',
    tag: 'tag',
    'due-week': 'dueDate',
  };

  // ⇒⇒⇒⇒⇒⇒⇒⇒ F 506 — THE DATE MODES WERE NEVER WIRED IN HERE.
  //
  // ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ MEASURED 2026-11-01: `groupRecords` COMPUTED
  // `field = modeToField[groupBy] ?? groupBy`, AND `modeToField` HAD NO
  // ENTRY FOR `'created-day'` OR `'created-month'`.** ⇒⇒□ ⇒⇒□ ⇒⇒□
  // ⇒⇒□ ⇒⇒□ **⇒⇒□ SO `field` BECAME THE LITERAL STRING `'created-day'`,
  // WHICH IS NOT IN THE `isDateField` LIST, AND EVERY RECORD FELL
  // THROUGH TO THE VALUE BRANCH AND LANDED IN "Uncategorized".** ⇒⇒□
  // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ THE MODE WAS SELECTABLE IN THE MENU AND IT
  // PRODUCED ONE COLUMN.** ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□
  //
  // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ AND THE FIX BELONGS HERE, NOT IN THE UI:
  // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ `granularityForMode` AND
  // `isDateGroupByMode` ALREADY EXISTED IN THIS FILE — THE AUTHOR WROTE
  // THEM AND THEN DID NOT CALL THEM.** ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ THE
  // HELPERS WERE THE SPECIFICATION AND THE CALL WAS THE MISSING WIRE.**
  const field = modeToField[groupBy] ?? groupBy;
  const isDateMode = isDateGroupByMode(groupBy);
  // ⇒⇒□ ⇒⇒□ ⇒⇒□ A DATE MODE READS THE RECORD'S OWN `createdAt`. ⇒⇒□
  // ⇒⇒□ ⇒⇒□ `field` BECOMES 'created' SO THE PROPERTY BRANCH IS NOT
  // ⇒⇒□ ⇒⇒□ TAKEN AND `readGroupDate` FALLS THROUGH TO `createdAt`.
  // ⇒⇒□ ⇒⇒□ ⇒⇒□
  // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ F 509 — EXCEPT FOR THE `'date'` MODE, WHICH
  // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ NAMES A PROPERTY. ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□
  // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ `dateFieldFor` DECIDES: A PSEUDO-FIELD GETS
  // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ `undefined` (THE FALLBACK SIGNAL); A NAMED PROPERTY
  // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ GETS ITS OWN ID (NO FALLBACK).**
  const resolvedDateField = isDateMode ? dateFieldFor(groupBy, groupByField, schema) : undefined;
  const effectiveField = isDateMode ? (resolvedDateField ?? 'created') : field;
  // ⇒⇒⇒⇒⇒⇒⇒⇒ F 509 — THE DATE CHECK IS SCHEMA-DRIVEN, NOT A LIST OF FIVE
  // STRINGS. ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ THE OLD LIST (`created` / `updated` /
  // ⇒⇒□ ⇒⇒□ ⇒⇒□ `createdAt` / `updatedAt` / `dueDate`) COULD NOT KNOW ABOUT
  // ⇒⇒□ ⇒⇒□ ⇒⇒□ A PROPERTY THE USER ADDED — SO A USER'S OWN DATE PROPERTY
  // ⇒⇒□ ⇒⇒□ ⇒⇒□ TOOK THE VALUE BRANCH AND THE CARD LANDED IN A COLUMN
  // ⇒⇒□ ⇒⇒□ ⇒⇒□ NAMED AFTER ITS RAW VALUE, NOT A DATE COLUMN AT ALL.**
  // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ THE SCHEMA SAYS `type: 'date'`. THAT IS THE
  // ⇒⇒□ ⇒⇒□ ⇒⇒□ ANSWER TO "ANY AD-HOC DATE PROPERTIES THE USER MAY ADD".**
  const isDateField = isDateProperty(effectiveField, schema);

  for (const record of records) {
    let key: string;
    let name: string;
    let sortKey: number;

    if (isDateField) {
      const date = readGroupDate(
        record,
        // ⇒⇒□ ⇒⇒□ ⇒⇒□ F 509 — THE RESOLVED FIELD IS PASSED THROUGH, AND IT
        // ⇒⇒□ ⇒⇒□ ⇒⇒□ IS `undefined` FOR A PSEUDO-FIELD (THE FALLBACK) AND
        // ⇒⇒□ ⇒⇒□ ⇒⇒□ THE PROPERTY ID FOR A NAMED ONE (NO FALLBACK).
        // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ THE OLD CODE PASSED `undefined` FOR EVERY DATE
        // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ MODE, WHICH IS WHY A USER'S OWN DATE PROPERTY
        // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ FELL BACK TO `createdAt`.
        resolvedDateField,
        // ⇒⇒□ ⇒⇒□ ⇒⇒□ AND THE SCHEMA, BECAUSE A `created_time` /
        // ⇒⇒□ ⇒⇒□ ⇒⇒□ `last_edited_time` PROPERTY IS NOT IN `properties`.
        schema,
      );
      if (!date) {
        key = UNCATEGORIZED_KEY;
        name = UNCATEGORIZED_KEY;
        // ⇒⇒⇒ THE UNCATEGORIZED COLUMN SORTS LAST, always. ⇒⇒⇒ ⇒⇒⇒⇒ ⇒⇒⇒
        // ⇒⇒⇒⇒ **⇒⇒⇒ IT IS NOT A DATE, AND IT MUST NOT PRETEND TO BE
        // ONE.** ⇒⇒⇒ ⇒⇒⇒⇒ ⇒⇒⇒ ⇒⇒⇒⇒ A sort key of `Number.MAX_SAFE_INTEGER`
        // puts it after every real bucket without pretending to be one.
        sortKey = Number.MAX_SAFE_INTEGER;
      } else if (granularity === 'relative') {
        // ⇒⇒⇒⇒⇒⇒⇒⇒ F 509 — THE RELATIVE WINDOWS ARE A LADDER, AND A DATE IN
        // NO WINDOW IS "Uncategorized". ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ THE RELATIVE
        // ⇒⇒□ ⇒⇒□ ⇒⇒□ WINDOWS ARE ORDERED OLDEST-FIRST ON THE BOARD — A
        // ⇒⇒□ ⇒⇒□ ⇒⇒□ TIMELINE READS LEFT TO RIGHT — AND THE SORT KEY IS
        // ⇒⇒□ ⇒⇒□ ⇒⇒□ THE WINDOW'S START, WHICH IS ASCENDING.** ⇒⇒□ ⇒⇒□
        // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ THE DATE BUCKETS BELOW SORT NEWEST-FIRST, SO THE
        // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ RELATIVE LADDER NEEDS ITS OWN ORDER — THE SORT
        // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ BELOW IS SIGNED BY THE GRANULARITY.
        const rel = relativeBucketKey(date, now);
        if (!rel) {
          key = UNCATEGORIZED_KEY;
          name = UNCATEGORIZED_KEY;
          sortKey = Number.MAX_SAFE_INTEGER;
        } else {
          key = rel.key;
          name = rel.name;
          sortKey = rel.sortKey;
        }
      } else {
        const b = dateBucketKey(date, granularity, startDayOfWeek);
        key = b.key;
        name = b.name;
        sortKey = b.sortKey;
      }
    } else {
      const raw = readProperty(record, field);
      if (raw === null || raw === undefined || raw === '') {
        key = UNCATEGORIZED_KEY;
        name = UNCATEGORIZED_KEY;
        sortKey = Number.MAX_SAFE_INTEGER;
      } else {
        name = valueLabel(raw);
        key = name;
        // ⇒⇒⇒ ⇒⇒⇒⇒ A NON-DATE BUCKET SORTS BY ITS NAME, and its sort key
        // is the name's position in a STABLE alphabet. ⇒⇒⇒ ⇒⇒⇒⇒ ⇒⇒⇒ ⇒⇒⇒⇒
        // ⇒⇒⇒ THE ORDER IS DETERMINISTIC, WHICH IS WHAT THE BOARD NEEDS.
        sortKey = 0;
      }
    }

    bucketFor(key, name, sortKey).records.push(record);
  }

  // ⇒⇒⇒ SORT THE BUCKETS. Dates newest-first (the dispatch says so). The
  // uncategorized bucket last. Non-date buckets by name, then by key.
  // ⇒⇒⇒⇒⇒⇒⇒⇒ F 509 — THE RELATIVE LADDER IS THE ONE EXCEPTION, AND IT IS
  // THE EXCEPTION ON PURPOSE. ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ "LAST 30 DAYS" →
  // "LAST 7 DAYS" → "TODAY" → "NEXT 7 DAYS" → "NEXT 30 DAYS" IS A TIMELINE,
  // ⇒⇒□ ⇒⇒□ ⇒⇒□ AND A TIMELINE READS LEFT TO RIGHT — OLDEST FIRST.** ⇒⇒□
  // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ THE DATE BUCKETS SORT NEWEST-FIRST BECAUSE A BOARD
  // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ OF DAYS IS SCANNED FOR "THE MOST RECENT", BUT A
  // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ LADDER OF WINDOWS HAS NO "MOST RECENT" — IT HAS A
  // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ START AND AN END, AND THE READER NEEDS THE START
  // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ FIRST.
  const isRelative = granularity === 'relative';
  const ordered = Array.from(buckets.values()).sort((a, b) => {
    if (a.key === UNCATEGORIZED_KEY) return 1;
    if (b.key === UNCATEGORIZED_KEY) return -1;
    if (a.sortKey !== b.sortKey) {
      // ⇒⇒□ ⇒⇒□ ⇒⇒□ RELATIVE: OLDEST FIRST. DATE BUCKETS: NEWEST FIRST.
      return isRelative ? a.sortKey - b.sortKey : b.sortKey - a.sortKey;
    }
    return a.key < b.key ? -1 : a.key > b.key ? 1 : 0;
  });

  return ordered.map((b, index) => ({
    id: b.key,
    name: b.name,
    color: b.key === UNCATEGORIZED_KEY ? '#94a3b8' : columnColour(index),
    records: b.records,
  }));
}

/**
 * F 504 — THE NEW GROUPING MODES.
 *
 * ⇒⇒⇒ THE POINT OF THE DISPATCH, MEASURED AGAINST THE EXISTING ONES.
 *
 * ⇒⇒⇒ 'created-day'   — one column per calendar day, newest first.
 * ⇒⇒⇒ 'created-month' — one column per calendar month, newest first.
 *
 * ⇒⇒⇒ ⇒⇒⇒⇒ ⇒⇒⇒ ⇒⇒⇒⇒ **⇒⇒⇒ THESE ARE THE DISPATCH'S `groupGranularity`
 * AS A `GroupByMode`, BECAUSE THE EXISTING VOCABULARY IS A `GroupByMode`
 * UNION.** ⇒⇒⇒ ⇒⇒⇒⇒ ⇒⇒⇒ ⇒⇒⇒⇒ A `groupGranularity` FIELD beside it would
 * be a second, parallel spelling of the same choice — and the codebase's own
 * history is a warning about exactly that (the `Database.ts` header records
 * the 3-SOURCE DRIFT that standardising on one champion removed).
 */
export type DateGroupByMode = 'created-day' | 'created-month' | 'date';

/**
 * Is this mode one of the date-granularity modes?
 */
export function isDateGroupByMode(mode: string): mode is DateGroupByMode {
  return mode === 'created-day' || mode === 'created-month' || mode === 'date';
}

/**
 * The granularity a date mode implies.
 *
 * ⇒⇒⇒⇒⇒⇒⇒⇒ F 509 — `'date'` HAS NO IMPLIED GRANULARITY. ⇒⇒□ ⇒⇒□ ⇒⇒□
 * **⇒⇒□ `'date'` MEANS "GROUP BY WHATEVER DATE PROPERTY THE VIEW NAMES" —
 * ⇒⇒□ ⇒⇒□ ⇒⇒□ AND HOW COARSELY IS THE VIEW'S OWN `groupGranularity`.** ⇒⇒□
 * ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ SO IT RETURNS `null` HERE, AND THE VIEW SUPPLIES
 * ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ THE GRANULARITY. THE LEGACY ALIASES STILL CARRY THEIR
 * ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ OWN, BECAUSE A SAVED VIEW FROM F 506 MUST KEEP
 * ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ RENDERING EXACTLY AS IT DID.**
 */
export function granularityForMode(mode: string): GroupGranularity | null {
  if (mode === 'created-day') return 'day';
  if (mode === 'created-month') return 'month';
  if (mode === 'date') return null;
  return null;
}

/**
 * F 509 — WHICH DATE DOES A MODE + FIELD PAIR MEAN?
 *
 * ⇒⇒⇒ THE PSEUDO-FIELDS AND THE USER'S OWN PROPERTIES TAKE DIFFERENT
 * PATHS, AND THE DIFFERENCE IS THE F 509 FALLBACK TRAP.
 *
 * ⇒⇒□ ⇒⇒□ **⇒⇒□ FOR A PSEUDO-FIELD, THE FUNCTION RETURNS `undefined` AS THE
 * FIELD — WHICH IS THE SIGNAL "FALL BACK TO `createdAt` / `updatedAt`".**
 * ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ FOR A NAMED PROPERTY, IT RETURNS THE PROPERTY ID
 * — AND `readGroupDate` THEN READS ONLY THAT PROPERTY, WITH NO FALLBACK.**
 *
 * @param mode     the view's `groupBy`
 * @param field    the view's `groupByField` — the user's chosen property
 * @param schema   the database's property definitions
 */
export function dateFieldFor(
  mode: string,
  field: string | undefined,
  schema?: readonly DatePropertyLike[] | null,
): string | undefined {
  if (mode === 'created-day' || mode === 'created-month') {
    // ⇒⇒□ ⇒⇒□ ⇒⇒□ THE LEGACY MODES ARE "GROUP BY THE RECORD'S OWN
    // ⇒⇒□ ⇒⇒□ ⇒⇒□ CREATION DATE" — SO THE FIELD IS `undefined`, WHICH IS
    // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ THE FALLBACK SIGNAL.
    return undefined;
  }
  if (field) {
    // ⇒⇒⇒⇒⇒⇒⇒⇒ F 509 — A PSEUDO-FIELD IS NOT A NAMED PROPERTY, AND IT
    // ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ NEEDS THE FALLBACK SIGNAL — WHICH IS `undefined`,
    // ⇒⇒□ ⇒⇒□ ⇒⇒□ NOT ITS OWN NAME.** ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ `'created'` IS
    // ⇒⇒□ ⇒⇒□ ⇒⇒□ A PSEUDO-FIELD THAT SAYS "THE RECORD'S OWN CREATION
    // ⇒⇒□ ⇒⇒□ ⇒⇒□ DATE" — IT IS NOT A PROPERTY IN `properties`, AND
    // ⇒⇒□ ⇒⇒□ ⇒⇒□ READING IT AS ONE WOULD FIND NOTHING AND PUT EVERY
    // ⇒⇒□ ⇒⇒□ ⇒⇒□ RECORD IN "Uncategorized".** ⇒⇒□ ⇒⇒□ ⇒⇒□
    if (PSEUDO_DATE_FIELDS.has(field)) return undefined;
    // ⇒⇒□ ⇒⇒□ ⇒⇒□ A NAMED PROPERTY — READ IT, AND ONLY IT.
    return field;
  }
  // ⇒⇒□ ⇒⇒□ ⇒⇒□ NO FIELD NAMED — AND THE SCHEMA MAY STILL SAY WHAT TO USE.
  // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ THE PSEUDO PROPERTIES ARE REAL PROPERTY TYPES
  // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ (`created_time` / `last_edited_time`), SO IF THE
  // ⇒⇒□ ⇒⇒□ ⇒⇒□ DATABASE DECLARES ONE, IT IS THE FIELD.
  if (schema) {
    const edited = schema.find((p) => p && p.type === 'last_edited_time');
    if (edited) return edited.id;
    const created = schema.find((p) => p && p.type === 'created_time');
    if (created) return created.id;
  }
  return undefined;
}
